/**
 * New Horizontal Swiper — 无 Hero 全屏缩放，首屏即为「收起」后的布局。
 * 对应区块：<new-horizontal-swiper-scroll sectionId="...">
 * 根节点：#new-horizontal-swiper-view-{sectionId}
 */
if (
  typeof window !== 'undefined' &&
  window.customElements &&
  !window.customElements.get('new-horizontal-swiper-scroll')
) {
  class NewHorizontalSwiperScroll extends HTMLElement {
    constructor() {
      super();
      this._id = this.getAttribute('sectionId');
      this.triggers = [];
      this.currentIndex = 0;
      this.isAnimating = false;

      this.heroInner = null;
      this.heroText = null;
      this.heroTitle = null;
      this.heroImgContent = null;
      this.heroContent = null;

      this.GAP = 20;
      this.HERO_HOLD_END = 0;
      this.LAST_HOLD = 0;

      this.HERO_CLASS_EXPANDED = 'hero-expanded';
      this.HERO_CLASS_COLLAPSED = 'hero-collapsed';

      this.paginations = [];
      this.scrollAccumulator = 0;
      this.SCROLL_THRESHOLD = 120;

      this.isActive = false;

      // 🚀 新增：进入可视范围的 1秒时间锁
      this.isTimeLocked = false;
      this.timeLockTimeout = null;

      this.wasActive = false;
      this.isRendered = false;
      this.resizeObserver = null;
    }

    disconnectedCallback() {
      this.triggers.forEach((t) => t.kill());
      this.triggers.length = 0;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      clearTimeout(this.timeLockTimeout);
    }

    // The element is hidden below 700px (class "sm:block hidden"); its cards then have no width.
    _isRendered() {
      return this.offsetWidth > 0 && this._getStep() > 0;
    }

    _canControl() {
      return this.isActive && !this.isAnimating;
    }

    connectedCallback() {
      setTimeout(() => this._init(), 300);
    }

    _applyFlatHeroState() {
      if (this.heroInner) {
        window.gsap.set(this.heroInner, {
          scale: 1,
          transformOrigin: 'center center',
          borderRadius: 12,
        });
      }
      if (this.heroText || this.heroTitle) {
        window.gsap.set([this.heroText, this.heroTitle].filter(Boolean), {
          opacity: 1,
          y: 0,
        });
      }
      if (this.heroImgContent) {
        window.gsap.set(this.heroImgContent, {
          opacity: 0,
          scale: 1,
          y: 0,
          pointerEvents: 'none',
        });
      }
      if (this.heroContent) {
        window.gsap.set(this.heroContent, {
          opacity: 1,
          y: 0,
          pointerEvents: 'auto',
        });
      }
      if (this.section) {
        this.section.classList.add(this.HERO_CLASS_COLLAPSED);
        this.section.classList.remove(this.HERO_CLASS_EXPANDED);
      }
    }

    _init() {
      const section = this.querySelector(`#new-horizontal-swiper-view-${this._id}`);
      if (!section || typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') {
        return;
      }

      this.section = section;
      this.wrapper = section.querySelector('.section-wrapper');
      this.cards = window.gsap.utils.toArray(section.querySelectorAll('.section-card'));
      this.stickyWrapper = section.querySelector('.sticky-wrapper');

      const heroCard = this.cards[0];
      if (heroCard) {
        this.heroInner = heroCard.querySelector('.card-inner');
        this.heroText = heroCard.querySelector('.card-rich-text');
        this.heroTitle = heroCard.querySelector('.cards-title');
        this.heroContent = heroCard.querySelector('.card-content');
      }

      this.heroImgContent = document.querySelector(`#img-content-${this._id}`);

      this.cards.forEach((card) => {
        window.gsap.set(card, { opacity: 1, scale: 1 });
      });

      this._applyFlatHeroState();

      this._initPaginations();
      this.isRendered = this._isRendered();
      this._createScrollTrigger();

      // A ScrollTrigger on the hidden element has a zero-length range at the top of the page, and a jump through it
      // snapped the page back up. It is built only while the element shows, and rebuilt when that changes.
      this.resizeObserver = new ResizeObserver(() => {
        const rendered = this._isRendered();
        if (rendered === this.isRendered) return;
        this.isRendered = rendered;
        this._createScrollTrigger();
        window.ScrollTrigger.refresh();
      });
      this.resizeObserver.observe(this);

      // window.requestAnimationFrame(() => {
      //   if (window.ScrollTrigger) window.ScrollTrigger.refresh();
      // });

      // window.addEventListener('resize', () => {
      //   if (window.ScrollTrigger) window.ScrollTrigger.refresh();
      // });
    }

    _initPaginations() {
      const paginations = this.section.querySelectorAll('.card-swiper-pagination');

      this.paginations = [];

      paginations.forEach((pagination) => {
        pagination.innerHTML = '';
        const bullets = [];

        for (let i = 0; i < this.cards.length; i++) {
          const bullet = document.createElement('div');
          bullet.className = 'swiper-pagination-bullet';

          const progress = document.createElement('span');
          progress.className = 'bullet-progress';

          bullet.appendChild(progress);
          pagination.appendChild(bullet);
          bullets.push({ bullet, progress });
        }

        this.paginations.push(bullets);
      });

      this._syncPagination(0, 0);
    }

    _syncPagination(index, progress = 0) {
      this.paginations.forEach((bullets) => {
        bullets.forEach((item, i) => {
          item.bullet.classList.toggle('swiper-pagination-bullet-active', i === index);
          item.progress.style.width = i === index ? `${progress * 100}%` : '0%';
        });
      });
    }

    _getStep() {
      return this.cards.length ? this.cards[0].offsetWidth + this.GAP : 0;
    }

    // Puts the cards, the pagination and the step state on `index` at once, cancelling any snap in flight.
    _syncToIndex(index) {
      window.gsap.killTweensOf(this.wrapper);
      this.isAnimating = false;
      this.scrollAccumulator = 0;
      this.currentIndex = index;
      window.gsap.set(this.wrapper, { x: -index * this._getStep() });
      this._syncPagination(index, 1);
    }

    _snapToIndex(index, self) {
      if (!this.isActive || !self || !this.wrapper || this._getStep() <= 0) return;

      this.isAnimating = true;
      this.currentIndex = index;
      const step = this._getStep();

      window.gsap.to(this.wrapper, {
        x: -index * step,
        duration: 0.8,
        ease: 'power3.out',
        onComplete: () => {
          this.isAnimating = false;
          this.scrollAccumulator = 0;
          this._syncPagination(index, 1);
        },
      });

      self.scroll(self.start + this.HERO_HOLD_END + index * step);
    }

    _createScrollTrigger() {
      this.triggers.forEach((t) => t.kill());
      this.triggers.length = 0;
      this.isActive = false;
      this.wasActive = false;
      if (!this._isRendered()) return;

      const step = this._getStep();
      const cardCount = this.cards.length - 1;

      this.HERO_HOLD_END = step * 0.8;
      this.LAST_HOLD = step * 0.6;

      const totalDistance = this.HERO_HOLD_END + step * cardCount + this.LAST_HOLD;

      const trigger = window.ScrollTrigger.create({
        trigger: this.stickyWrapper,
        start: 'top top',
        end: `+=${totalDistance}`,
        pin: true,
        scrub: false,

        // 🚀 新增：在进入可视范围时，启动 1000ms 的倒计时锁
        onEnter: () => {
          this.isActive = true;
          this.isTimeLocked = true;
          clearTimeout(this.timeLockTimeout);
          this.timeLockTimeout = setTimeout(() => {
            this.isTimeLocked = false;
          }, 1000);
        },
        onEnterBack: () => {
          this.isActive = true;
          this.isTimeLocked = true;
          clearTimeout(this.timeLockTimeout);
          this.timeLockTimeout = setTimeout(() => {
            this.isTimeLocked = false;
          }, 1000);
        },
        
        // 🚀 新增：离开可视范围时，重置并清除定时器
        onLeave: () => {
          this.isActive = false;
          this.isAnimating = false;
          this.scrollAccumulator = 0;
          clearTimeout(this.timeLockTimeout);
          this.isTimeLocked = false;
        },
        onLeaveBack: () => {
          this.isActive = false;
          this.isAnimating = false;
          this.scrollAccumulator = 0;
          clearTimeout(this.timeLockTimeout);
          this.isTimeLocked = false;
        },

        onUpdate: (self) => {
          // A jump that lands outside the pinned range (anchor tabs, Home/End) still updates once on the way;
          // snapping then would pull the page back into the section, so only steps taken inside it count.
          if (!self.isActive) {
            this.wasActive = false;
            return;
          }
          // First update inside the range: if a jump landed past the first card, take the card from the scroll
          // position instead of snapping from a stale index. Regular scrolling in from either end matches already.
          if (!this.wasActive) {
            this.wasActive = true;
            const entryScroll = self.scroll() - self.start;
            const entryIndex =
              entryScroll <= this.HERO_HOLD_END || cardCount < 1
                ? 0
                : window.gsap.utils.clamp(0, cardCount, Math.round((entryScroll - this.HERO_HOLD_END) / step));
            if (entryIndex !== this.currentIndex) {
              this._syncToIndex(entryIndex);
              return;
            }
          }
          if (!this._canControl()) return;

          const scrollY = self.scroll() - self.start;

          /* 首段：仅停留第一张卡片 */
          if (scrollY <= this.HERO_HOLD_END) {
            this.currentIndex = 0;
            window.gsap.set(this.wrapper, { x: 0 });
            this.scrollAccumulator = 0;
            const p = this.HERO_HOLD_END > 0 ? Math.min(1, scrollY / this.HERO_HOLD_END) : 1;
            this._syncPagination(0, p);
            return;
          }

          // 🚀 核心修改：如果 1 秒的锁还没解开，清空用户的滚动累加并强行返回
          // 这意味着在前 1 秒内，用户怎么往下滚都没用，画面会乖乖停在第一张卡片
          if (this.isTimeLocked) {
            this.scrollAccumulator = 0;
            return;
          }

          /* 仅 1 张卡片时无横向切换，避免 clamp(1, 0, x) 异常 */
          if (cardCount < 1) return;

          const base = this.HERO_HOLD_END + this.currentIndex * step;
          const delta = scrollY - base;

          const isLast = this.currentIndex === cardCount;
          const isScrollingUp = delta < 0;

          const threshold =
            isLast && isScrollingUp ? this.SCROLL_THRESHOLD * 0.4 : this.SCROLL_THRESHOLD;

          this.scrollAccumulator += isLast && isScrollingUp ? delta * 1.5 : delta;

          if (Math.abs(this.scrollAccumulator) < threshold) return;

          const dir = this.scrollAccumulator > 0 ? 1 : -1;
          const next = window.gsap.utils.clamp(0, cardCount, this.currentIndex + dir);

          if (next !== this.currentIndex) {
            this._snapToIndex(next, self);
          }
        },
      });

      this.triggers.push(trigger);
    }
  }

  window.customElements.define('new-horizontal-swiper-scroll', NewHorizontalSwiperScroll);
}